import { REASONING_EFFORTS, type ReasoningEffort } from '@developerz/domain';
import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Select, type SelectOption } from './Select';

// A route's reasoning effort (plan 101 slice 17, themed epic #5620 slice 02):
// Model default, then every domain level lowest first — the options come from
// `REASONING_EFFORTS`, never a local list. The unset value is `''`, a caller
// sends it as `null`: there is no "off" level (see the domain vocabulary's
// header). Presenter; the caller owns the state, and `nextRouteRow`'s
// `storable` rule owns "needs provider + model"
// (`packages/domain/src/value-objects/route-row.ts`). `error` is the API's own
// 422 `detail`, shown as this field's error so it sits next to the control.
//
// `''` is itself a real, listed, pickable `Select` option (`account_keys
// .routes.effort.none`, en "Model default") — the SAME string this field's
// `value` uses for "no override", so picking it back from a stored level
// resets the pin the same way a fresh mount does.

const EFFORT_OPTIONS: readonly SelectOption<ReasoningEffort | ''>[] = [
  { value: '', label: t('account_keys.routes.effort.none') },
  ...REASONING_EFFORTS.map((level) => ({
    value: level,
    label: t(`account_keys.routes.effort.${level}`),
  })),
];

export function RouteEffortField(props: {
  /** The field's own id (also the prefix of its hint/error ids) — the caller's,
   * since a drawer can mount this more than once (the org purpose, a per-repo
   * row, a Coder task-tag row, #5436): a hardcoded id would duplicate. */
  readonly id: string;
  readonly value: ReasoningEffort | '';
  readonly onChange: (value: ReasoningEffort | '') => void;
  readonly error: string | null;
  /** Locked (no `write:billing`, or a caller-held busy state). A `<fieldset
   * disabled>` ancestor already covers real browsers; this is the explicit
   * seam a test environment without that cascade (or a lone mount) can read. */
  readonly disabled?: boolean;
}): JSX.Element {
  return (
    <Field
      label={t('account_keys.routes.effort_label')}
      id={props.id}
      desc={t('account_keys.routes.effort_desc')}
      labelTitle={t('account_keys.routes.effort_hint')}
      error={props.error}
    >
      {(a11y) => (
        <Select
          a11y={a11y}
          label={t('account_keys.routes.effort_label')}
          value={props.value}
          options={EFFORT_OPTIONS}
          disabled={props.disabled ?? false}
          onChange={props.onChange}
          block
        />
      )}
    </Field>
  );
}
